import { handleActions } from 'redux-actions';

import { USER_ACTION_TYPES } from './constants';
import UserState from './UserState';

import type { Action } from '../types';
import type { ReceiveUserPayload } from './types';
import type { ReceiveTempUserPayload } from './types';

const initialState = new UserState({
  isLoggedIn: false,
  isSettingUpAuth: true
});

const actions = {
  [USER_ACTION_TYPES.AUTH_STATE_CHANGE]: handleAuthStateChange,
  [USER_ACTION_TYPES.SETUP_AUTH]: handleSetupAuth,
  [USER_ACTION_TYPES.REGISTER_USER]: registerUser,
  [USER_ACTION_TYPES.FETCH_USER]: fetchUser,
  [USER_ACTION_TYPES.RECEIVE_USER]: receiveUser,
  [USER_ACTION_TYPES.REGISTER_TEMP_USER]: registerTempUser,
  [USER_ACTION_TYPES.RECEIVE_TEMP_USER]: receiveTempUser,
  [USER_ACTION_TYPES.FETCH_LOGOUT]: fetchLogout,
  [USER_ACTION_TYPES.RECEIVE_LOGOUT]: receiveLogout,
  [USER_ACTION_TYPES.SET_AVATAR]: setAvatar,
  [USER_ACTION_TYPES.UPDATE_USER]: updateUser,
  [USER_ACTION_TYPES.RECEIVE_UPDATED_USER]: receiveUpdateUser,
  [USER_ACTION_TYPES.RECEIVE_INCORRECT_PASSWORD]: receiveIncorrectPassword,
  [USER_ACTION_TYPES.CLEAR_INCORRECT_PASSWORD]: clearIncorrectPassword,
  [USER_ACTION_TYPES.RECEIVE_TOUCH_ID_NOT_SUPPORTED]: receiveTouchIdNotSupportedError,
  [USER_ACTION_TYPES.RECEIVE_CLEAR_TOUCH_ID_NOT_SUPPORTED]: clearTouchIdNotSupportedError,
  [USER_ACTION_TYPES.RECEIVE_TOUCH_ID_CANCELLED]: receiveTouchIdCancelled,
  [USER_ACTION_TYPES.RECEIVE_INCORRECT_TOUCH_ID]: receiveIncorrectTouchId,
  [USER_ACTION_TYPES.RECEIVE_INVALID_EMAIL]: receiveInvalidEmailError,
  [USER_ACTION_TYPES.CLEAR_INVALID_EMAIL]: clearInvalidEmailError,
  [USER_ACTION_TYPES.RECEIVE_WEAK_PASSWORD]: receiveWeakPassword,
  [USER_ACTION_TYPES.CLEAR_WEAK_PASSWORD]: clearWeakPassword,
  [USER_ACTION_TYPES.RECEIVE_UNAVAILABLE_EMAIL]: receiveUnavailableEmailError,
  [USER_ACTION_TYPES.CLEAR_UNAVAILABLE_EMAIL]: clearUnavailableEmailError,
  [USER_ACTION_TYPES.CREATE_CUSTOMER]: createCustomer,
  [USER_ACTION_TYPES.RECEIVE_CUSTOMER]: receiveCustomer,
  [USER_ACTION_TYPES.CREATE_SUBSCRIPTION]: createSubscription,
  [USER_ACTION_TYPES.RECEIVE_SUBSCRIPTION]: receiveSubscription,
  [USER_ACTION_TYPES.UPDATE_SUBSCRIPTION]: createSubscription,
  [USER_ACTION_TYPES.RECEIVE_UPDATED_SUBSCRIPTION]: receiveSubscription,
  [USER_ACTION_TYPES.CANCEL_SUBSCRIPTION]: cancelSubscription,
  [USER_ACTION_TYPES.UPDATE_CUSTOMER]: createCustomer,
  [USER_ACTION_TYPES.RECEIVE_UPDATED_CUSTOMER]: receiveCustomer,
  [USER_ACTION_TYPES.FETCH_COUPON]: fetchCoupon,
  [USER_ACTION_TYPES.RECEIVE_INCORRECT_COUPON]: receiveIncorrectCoupon,
  [USER_ACTION_TYPES.RECEIVE_COUPON]: receiveCoupon
};

function handleAuthStateChange(state: UserState): UserState {
  return state
    .setIsLoggingIn(true)
    .setIsSettingUpAuth(false);
}

function handleSetupAuth(state: UserState): UserState {
  return state.setIsSettingUpAuth(true);
}

function fetchUser(state: UserState): UserState {
  return state.setIsLoggingIn(true);
}

function registerUser(state: UserState): UserState {
  return state.setIsLoggingIn(true);
}

// eslint-disable-next-line object-curly-spacing
function receiveUser(state: UserState, { payload }: Action<ReceiveUserPayload>): UserState {
  if (!payload) {
    return state;
  }
  return state
    .setIsLoggingIn(false)
    .setIsLoggedIn(true)
    .setUser(payload.user)
    .setIsUpdatingUser(false);
}

function registerTempUser(state: UserState): UserState {
  return state.setIsAddingUser(true);
}

function receiveTempUser(state: UserState, { payload }: Action<ReceiveTempUserPayload>): UserState {
  if (!payload) {
    return state;
  }
  return state
    .setIsAddingUser(false)
    .setUser(payload.tempUser)
    .setIsUpdatingUser(false);
}

function fetchLogout(state: UserState): UserState {
  return state.setIsLoggingIn(true);
}

function receiveLogout(state: UserState): UserState {
  return state.removeUser()
    .setIsLoggingIn(false)
    .setIsLoggedIn(false);
}

function setAvatar(state: UserState): UserState {
  return state.setIsUpdatingUser(true);
}

function updateUser(state: UserState): UserState {
  return state.setIsUpdatingUser(true);
}

function receiveUpdateUser(state: UserState): UserState {
  return state.setIsUpdatingUser(false);
}

function receiveIncorrectPassword(state: UserState): UserState {
  return state.setShowIncorrectPasswordError(true);
}

function clearIncorrectPassword(state: UserState): UserState {
  return state.setShowIncorrectPasswordError(false);
}

function receiveUnavailableEmailError(state: UserState): UserState {
  return state.setShowUnavailableEmailError(true);
}

function clearUnavailableEmailError(state: UserState): UserState {
  return state.setShowUnavailableEmailError(false);
}

function receiveTouchIdNotSupportedError(state: UserState): UserState {
  return state
    .setShowTouchIdNotSupportedError(true)
    .setIsLoggingIn(false);
}

function clearTouchIdNotSupportedError(state: UserState): UserState {
  return state.setShowTouchIdNotSupportedError(false);
}

function receiveTouchIdCancelled(state: UserState): UserState {
  return state.setIsLoggingIn(false);
}

function receiveIncorrectTouchId(state: UserState): UserState {
  return state
    .setShowIncorrectPasswordError(true)
    .setIsLoggingIn(false);
}

function receiveInvalidEmailError(state: UserState): UserState {
  return state.setShowInvalidEmailError(true);
}

function clearInvalidEmailError(state: UserState): UserState {
  return state.setShowInvalidEmailError(false);
}

function receiveWeakPassword(state: UserState): UserState {
  return state.setShowWeakPasswordError(true);
}

function clearWeakPassword(state: UserState): UserState {
  return state.setShowWeakPasswordError(false);
}

function createCustomer(state: UserState): UserState {
  return state.setIsCreatingCustomer(true);
}

function receiveCustomer(state: TimelineState): UserState {
  return state.setIsCreatingCustomer(false);
}

function createSubscription(state: UserState): UserState {
  return state.setIsCreatingSubscription(true);
}

function receiveSubscription(state: TimelineState): UserState {
  return state.setIsCreatingSubscription(false);
}

function cancelSubscription(state: TimelineState): UserState {
  return state.setIsCreatingSubscription(true);
}

function fetchCoupon(state: UserState): UserState {
  return state.setIsFetchingCoupon(true);
}

function receiveIncorrectCoupon(state: UserState): UserState {
  return state
    .setShowIncorrectCouponError(true)
    .setIsFetchingCoupon(false)
    .setShowCouponSuccess(false);
}

function receiveCoupon(state: UserState): UserState {
  return state
    .setIsFetchingCoupon(false)
    .setShowIncorrectCouponError(false)
    .setShowCouponSuccess(true);
}

export default handleActions(actions, initialState);



// WEBPACK FOOTER //
// ./src/redux/user/reducer.js